<script setup lang="ts">
import { computed } from 'vue';

interface UsageChartPoint {
  date: string;
  requests: number;
  tokens: number;
  cost_usd: number;
}

const props = withDefaults(
  defineProps<{
    points: UsageChartPoint[];
    height?: number;
  }>(),
  {
    height: 320,
  },
);

const VIEW_WIDTH = 760;
const PADDING_TOP = 18;
const PADDING_RIGHT = 24;
const PADDING_BOTTOM = 42;
const PADDING_LEFT = 52;

const plotWidth = computed<number>(() => VIEW_WIDTH - PADDING_LEFT - PADDING_RIGHT);
const plotHeight = computed<number>(() => props.height - PADDING_TOP - PADDING_BOTTOM);
const baseY = computed<number>(() => PADDING_TOP + plotHeight.value);

const maxRequests = computed<number>(() =>
  props.points.reduce((max, point) => Math.max(max, point.requests), 0),
);

const yStep = computed<number>(() => Math.max(1, Math.ceil(maxRequests.value / 4)));
const yMax = computed<number>(() => yStep.value * 4);
const yTicks = computed<number[]>(() =>
  Array.from({ length: 5 }, (_, index) => index * yStep.value),
);

const yGuides = computed<Array<{ tick: number; y: number }>>(() =>
  yTicks.value.map((tick) => ({
    tick,
    y: baseY.value - (tick / Math.max(1, yMax.value)) * plotHeight.value,
  })),
);

interface PlotPoint extends UsageChartPoint {
  x: number;
  y: number;
}

const plotPoints = computed<PlotPoint[]>(() => {
  const count = props.points.length;
  return props.points.map((point, index) => {
    const ratio = count <= 1 ? 0.5 : index / (count - 1);
    const x = PADDING_LEFT + ratio * plotWidth.value;
    const y = baseY.value - (point.requests / Math.max(1, yMax.value)) * plotHeight.value;
    return { ...point, x, y };
  });
});

const linePath = computed<string>(() => {
  if (plotPoints.value.length === 0) return '';
  return plotPoints.value
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`)
    .join(' ');
});

const areaPath = computed<string>(() => {
  if (plotPoints.value.length === 0) return '';
  const first = plotPoints.value[0];
  const last = plotPoints.value[plotPoints.value.length - 1];
  return `${linePath.value} L ${last.x.toFixed(2)} ${baseY.value.toFixed(2)} L ${first.x.toFixed(2)} ${baseY.value.toFixed(2)} Z`;
});

const activePoints = computed<PlotPoint[]>(() => plotPoints.value.filter((point) => point.requests > 0));

interface XAxisLabel {
  key: string;
  x: number;
  text: string;
}

const xAxisLabels = computed<XAxisLabel[]>(() => {
  if (plotPoints.value.length === 0) return [];
  const candidates = [
    plotPoints.value[0],
    plotPoints.value[Math.floor((plotPoints.value.length - 1) / 2)],
    plotPoints.value[plotPoints.value.length - 1],
  ];
  const seen = new Set<string>();
  const labels: XAxisLabel[] = [];

  for (const point of candidates) {
    if (seen.has(point.date)) continue;
    seen.add(point.date);
    labels.push({
      key: point.date,
      x: point.x,
      text: formatShortDate(point.date),
    });
  }

  return labels;
});

const totalRequests = computed<number>(() =>
  props.points.reduce((sum, point) => sum + point.requests, 0),
);

function formatShortDate(date: string): string {
  const [, month = '00', day = '00'] = date.split('-');
  return `${month}-${day}`;
}
</script>

<template>
  <div class="w-full overflow-hidden">
    <svg
      class="w-full h-auto"
      :viewBox="`0 0 ${VIEW_WIDTH} ${props.height}`"
      role="img"
      aria-label="Daily API requests over the last 30 days"
    >
      <defs>
        <linearGradient id="usage-area-gradient" x1="0" y1="0" x2="0" :y2="props.height">
          <stop offset="0%" stop-color="#171717" stop-opacity="0.10" />
          <stop offset="100%" stop-color="#171717" stop-opacity="0.01" />
        </linearGradient>
      </defs>

      <g>
        <line
          v-for="guide in yGuides"
          :key="`grid-${guide.tick}`"
          :x1="PADDING_LEFT"
          :x2="VIEW_WIDTH - PADDING_RIGHT"
          :y1="guide.y"
          :y2="guide.y"
          stroke="#e5e5e5"
          stroke-width="1"
        />

        <text
          v-for="guide in yGuides"
          :key="`label-${guide.tick}`"
          :x="PADDING_LEFT - 12"
          :y="guide.y + 4"
          text-anchor="end"
          font-size="12"
          fill="#a3a3a3"
        >
          {{ guide.tick }}
        </text>

        <line
          :x1="PADDING_LEFT"
          :x2="VIEW_WIDTH - PADDING_RIGHT"
          :y1="baseY"
          :y2="baseY"
          stroke="#d4d4d4"
          stroke-width="1"
        />

        <path v-if="areaPath" :d="areaPath" fill="url(#usage-area-gradient)" />
        <path
          v-if="linePath"
          :d="linePath"
          fill="none"
          stroke="#171717"
          stroke-width="3"
          stroke-linecap="round"
          stroke-linejoin="round"
        />

        <circle
          v-for="point in activePoints"
          :key="`${point.date}-${point.requests}`"
          :cx="point.x"
          :cy="point.y"
          r="4"
          fill="#ffffff"
          stroke="#171717"
          stroke-width="2"
        >
          <title>{{ `${point.date}: ${point.requests} requests` }}</title>
        </circle>

        <text
          v-for="label in xAxisLabels"
          :key="label.key"
          :x="label.x"
          :y="props.height - 12"
          text-anchor="middle"
          font-size="12"
          fill="#a3a3a3"
        >
          {{ label.text }}
        </text>
      </g>
    </svg>

    <div class="mt-3 flex items-center justify-between gap-3 text-xs text-muted flex-wrap">
      <span>Daily requests</span>
      <span>{{ totalRequests.toLocaleString('en-US') }} requests in the chart window</span>
    </div>
  </div>
</template>